import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Heart, HeartHandshake, Flower2, Waves, Compass, Feather, Pin, PinOff, Volume2, VolumeX } from "lucide-react";
import { endpoints } from "@/lib/api";
import SoulOrb from "@/components/SoulOrb";
import {
  getPinnedAffirmation, pinAffirmation, clearPinnedAffirmation,
} from "@/lib/pinnedAffirmation";
import { canSpeak, speak, stopSpeaking } from "@/lib/readAloud";
import { EDITORIAL } from "@/lib/editorialImages";
import { setCarryingAffirmation } from "@/lib/carryingAffirmation";

/**
 * AffirmationsLibrary — the Mind → Daily Affirmations tab.
 *
 * A curated, non-personalized library of affirmations grouped by theme.
 * Tap the small feather beside any line to seed it as a prompt into the
 * Journal so today's reflection can begin exactly where the words landed.
 */

const CATEGORIES = [
  {
    slug: "self-love",
    title: "Self-Love + Worthiness",
    Icon: Heart,
    affirmations: [
      "I deserve a life that feels good to live.",
      "I honor myself with compassion, patience, and care.",
      "I am worthy of love, support, and good things.",
      "I choose to treat myself with the same kindness I offer others.",
      "I am enough exactly as I am, and I am allowed to evolve.",
    ],
  },
  {
    slug: "abundance",
    title: "Abundance + Receiving",
    Icon: Flower2,
    affirmations: [
      "I am open to receiving abundance in all forms.",
      "Good things flow to me because I am aligned with them.",
      "I trust that life is expanding for me in ways I can't yet see.",
      "I welcome opportunities that support my growth and wellbeing.",
      "I allow abundance to enter my life without resistance.",
    ],
  },
  {
    slug: "inner-peace",
    title: "Inner Peace + Emotional Regulation",
    Icon: Waves,
    affirmations: [
      "I choose peace over chaos and clarity over confusion.",
      "I release what no longer serves my mind, body, or spirit.",
      "I respond with calm strength, even when challenged.",
      "I protect my inner world by mastering my reactions.",
      "I create space for stillness, reflection, and renewal.",
    ],
  },
  {
    slug: "purpose",
    title: "Purpose + Alignment",
    Icon: Compass,
    affirmations: [
      "I am becoming the person my future self will be proud of.",
      "I act with intention and move with purpose.",
      "I trust myself to make aligned decisions.",
      "I am building a life rooted in clarity, truth, and self-respect.",
      "I am worthy of the discipline required to create the life I desire.",
    ],
  },
  {
    slug: "aligned-love",
    title: "Attracting Aligned Love",
    Icon: HeartHandshake,
    affirmations: [
      "I am worthy of a partner who shows up with respect and consistency.",
      "I attract relationships that feel safe, supportive, and aligned.",
      "I am my true self, and the right person recognizes my value.",
      "I am open to receiving love that matches my growth and intentions.",
      "The partner meant for me is already moving toward my life.",
    ],
  },
];

async function seedAffirmation(text) {
  // Legacy helper kept so any older call-site compiles. The current
  // "Carry into Journal" flow uses `handleSeed` → navigates to Journal
  // and renders a temporary context panel above the composer; nothing
  // writes a journal entry on behalf of the member any more.
  return text;
}

export default function AffirmationsLibrary() {
  const [pulseSeed, setPulseSeed] = useState(0);
  const [pinned, setPinned] = useState(() => getPinnedAffirmation());
  const [readingText, setReadingText] = useState("");

  // A pool of every library line so the "An affirmation for you" moment
  // (shown when nothing is pinned) can quietly rotate through the whole
  // library instead of reseeding from an unrelated bank.
  const allLines = useMemo(() => {
    const out = [];
    for (const cat of CATEGORIES) {
      for (const line of cat.affirmations) out.push({ text: line, category: cat.title });
    }
    return out;
  }, []);
  const [suggestionIdx, setSuggestionIdx] = useState(() =>
    allLines.length ? Math.floor(Math.random() * allLines.length) : 0,
  );
  const [rolling, setRolling] = useState(false);
  const suggestion = allLines[suggestionIdx] || null;
  const navigate = useNavigate();
  const rollAnother = () => {
    if (allLines.length <= 1) return;
    setRolling(true);
    // Pick a different index so "Another →" always lands somewhere new.
    let next = suggestionIdx;
    while (next === suggestionIdx) next = Math.floor(Math.random() * allLines.length);
    setSuggestionIdx(next);
    setTimeout(() => setRolling(false), 150);
  };

  // Reflect external pin changes (Home card release, other tabs).
  useEffect(() => {
    const refresh = () => setPinned(getPinnedAffirmation());
    window.addEventListener("pinned-affirmation-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("pinned-affirmation-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  React.useEffect(() => () => stopSpeaking(), []);

  const toggleRead = (text) => {
    if (readingText === text) { stopSpeaking(); setReadingText(""); return; }
    if (!canSpeak()) { toast.error("Read aloud isn't available on this browser."); return; }
    const ok = speak(text, { rate: 0.85, onEnd: () => setReadingText("") });
    if (ok) setReadingText(text);
  };

  const handleSeed = (text, categoryTitle = "") => {
    // Stash the affirmation in sessionStorage; the Journal page reads it
    // once on mount and renders a temporary "Today I'm carrying" panel
    // above the composer. We never auto-create a journal entry on the
    // member's behalf.
    setCarryingAffirmation({ text, category: categoryTitle });
    setPulseSeed((p) => p + 1);
    navigate("/journal");
  };

  const handlePin = (text, categoryTitle) => {
    if (pinned?.text === text) {
      clearPinnedAffirmation();
      setPinned(null);
      toast.success("Unpinned.");
    } else {
      const p = pinAffirmation({ text, category: categoryTitle });
      setPinned(p);
      setPulseSeed((v) => v + 1);
      toast.success("Pinned to Home for seven days.");
    }
  };

  return (
    <section className="space-y-10 reveal reveal-1" data-testid="affirmations-library">
      {/* Header + why-it-works + orb */}
      <header className="grid gap-6 sm:grid-cols-[auto,1fr] items-start">
        <div className="flex justify-center sm:justify-start" data-testid="affirmations-orb-wrap">
          <SoulOrb
            size="md"
            pulseSeed={pulseSeed}
            ariaLabel="Soul orb — pulses when an affirmation is carried into your Journal"
            testid="affirmations-orb"
          />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
            <Heart size={12} /> Daily Affirmations
          </div>
          <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic leading-tight">
            Words that rewire you.
          </h2>
          <p
            className="mt-4 max-w-2xl text-[15px] text-[hsl(var(--fg))]/85 leading-relaxed"
            data-testid="affirmations-intro"
          >
            Affirmations are powerful because they rewire the brain through repetition.
            Each time you repeat an intentional statement, you strengthen a new neural
            pathway. Over time, these pathways become your automatic thoughts, shaping
            your identity, your emotions, and your actions. Affirmations help you
            replace old patterns with new ones, regulate your nervous system, and
            create a mindset aligned with self-love, abundance, and intentional living.
          </p>
        </div>
      </header>

      {/* ORIENTATION — Choose / Carry / Reflect, placed BEFORE the library
          so the member learns the practice before entering the catalogue.
          Three editorial columns on desktop, stacked on mobile. */}
      <section
        className="pt-2 reveal"
        aria-label="How to use Daily Affirmations"
        data-testid="affirmations-orientation"
      >
        <div className="grid gap-8 md:gap-6 md:grid-cols-3 md:divide-x md:divide-[hsl(var(--line))]/60">
          {[
            { label: "Choose",  body: "Select the words that meet you where you are." },
            { label: "Carry",   body: "Keep the affirmation with you throughout your day." },
            { label: "Reflect", body: "Notice where your thoughts, choices, and actions begin to shift." },
          ].map((step) => (
            <div
              key={step.label}
              className="md:px-6 text-center md:text-left"
              data-testid={`affirmations-step-${step.label.toLowerCase()}`}
            >
              <div className="text-sm font-bold uppercase tracking-[0.3em] text-[hsl(var(--clay))]">
                {step.label}
              </div>
              <p className="mt-3 font-display italic text-[17px] leading-snug text-[hsl(var(--fg))]/90">
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Four category cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {CATEGORIES.map((cat) => {
          const Icon = cat.Icon;
          return (
            <div
              key={cat.slug}
              className="rounded-md border hairline bg-[hsl(var(--paper))] p-6 md:p-7"
              data-testid={`affirmations-card-${cat.slug}`}
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--clay))]">
                <span
                  aria-hidden
                  className="grid place-items-center w-6 h-6 rounded-full bg-[hsl(var(--clay))]/12 text-[hsl(var(--clay))]"
                >
                  <Icon size={12} />
                </span>
                <span data-testid={`affirmations-card-title-${cat.slug}`}>{cat.title}</span>
              </div>

              <ul className="mt-5 space-y-4">
                {cat.affirmations.map((line, i) => {
                  const isPinned = pinned?.text === line;
                  const isReading = readingText === line;
                  return (
                    <li
                      key={i}
                      className="group flex items-start gap-3"
                      data-testid={`affirmations-line-${cat.slug}-${i}`}
                    >
                      <span
                        aria-hidden
                        className="mt-2.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[hsl(var(--terracotta))]/70"
                      />
                      <p className="flex-1 font-display italic text-[16px] leading-snug text-[hsl(var(--fg))]">
                        {line}
                      </p>
                      {canSpeak() && (
                        <button
                          type="button"
                          onClick={() => toggleRead(line)}
                          aria-label={isReading ? "Stop reading" : "Read aloud"}
                          data-testid={`affirmations-speaker-${cat.slug}-${i}`}
                          className={`p-1 rounded-full tab-link ${
                            isReading
                              ? "text-[hsl(var(--terracotta))]"
                              : "text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--terracotta))]"
                          }`}
                        >
                          {isReading ? <VolumeX size={13} /> : <Volume2 size={13} />}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handlePin(line, cat.title)}
                        aria-label={isPinned ? "Unpin from Home" : "Pin to Home for a week"}
                        data-testid={`affirmations-pin-${cat.slug}-${i}`}
                        className={`p-1 rounded-full tab-link ${
                          isPinned
                            ? "text-[hsl(var(--terracotta))]"
                            : "text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--terracotta))]"
                        }`}
                      >
                        {isPinned ? <PinOff size={13} /> : <Pin size={13} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSeed(line, cat.title)}
                        aria-label="Carry into Journal"
                        data-testid={`affirmations-seed-${cat.slug}-${i}`}
                        className="p-1 rounded-full text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--terracotta))] tab-link"
                      >
                        <Feather size={13} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <p
        className="text-center text-[13px] italic text-[hsl(var(--muted-foreground))]"
        data-testid="affirmations-choose-instruction"
      >
        <span className="block text-[10px] not-italic uppercase tracking-[0.26em] text-[hsl(var(--terracotta))] mb-1">
          Choose what stays with you
        </span>
        Pin an affirmation to carry it with you for the week, or use the feather
        to take it into your Journal.
      </p>

      {/* ONE editorial image — the quiet bridge between READING an
          affirmation and LIVING from it. Not another hero; wide and
          relatively shallow with generous whitespace. */}
      <figure
        className="relative w-full overflow-hidden rounded-md reveal h-[220px] sm:h-[260px] md:h-[320px] lg:h-[380px]"
        data-testid="affirmations-transition-image"
      >
        <img
          src={EDITORIAL.affirmationsTransition}
          alt="An open journal and warm coffee in soft morning light — a reflective moment."
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(0.95) contrast(0.98)" }}
        />
      </figure>

      {/* YOUR AFFIRMATION / AN AFFIRMATION FOR YOU — the emotional
          centerpiece. Reuses the existing pinned state + journal seed.
          Appears directly after the editorial image — no additional
          explanatory section between the image and this moment. */}
      <YourAffirmationMoment
        pinned={pinned}
        onCarry={(text) => handleSeed(text, (pinned?.category || suggestion?.category || ""))}
        onPickAnother={rollAnother}
        onUnpin={() => { clearPinnedAffirmation(); setPinned(null); toast("Released."); }}
        rolling={rolling}
        suggestion={suggestion}
      />
    </section>
  );
}

/**
 * YourAffirmationMoment — the lower centerpiece. Two visual states:
 *
 *   PINNED           → "YOUR AFFIRMATION" + "Carry this with you." +
 *                      Carry into Journal · Choose another
 *   NOT PINNED       → "AN AFFIRMATION FOR YOU" + a surfaced line from
 *                      the library + Keep for the week · Carry into
 *                      Journal · Another
 *
 * Deliberately quiet — no streaks, no celebratory animations, no
 * achievement language. The reward is the practice itself.
 */
function YourAffirmationMoment({ pinned, suggestion, onCarry, onPickAnother, onUnpin, rolling }) {
  // When pinned, the pinned text IS the moment. When not pinned, the
  // suggestion rotates each time the member taps "Another".
  const text = pinned?.text || suggestion?.text || "";
  const category = pinned?.category || suggestion?.category || "";

  if (!text) return null;

  return (
    <section
      className="relative overflow-hidden rounded-md border hairline bg-gradient-to-br from-[hsl(var(--sand))]/50 via-[hsl(var(--paper))] to-[hsl(var(--paper))] p-7 md:p-10 reveal"
      data-testid={pinned ? "affirmations-your-affirmation" : "affirmations-for-you"}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -right-16 w-64 h-64 rounded-full opacity-[0.08]"
        style={{ background: "radial-gradient(circle, hsl(var(--terracotta)) 0, transparent 65%)" }}
      />
      <div className="relative max-w-3xl">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--terracotta))]">
          {pinned ? <Pin size={11} /> : <Heart size={11} />}
          <span data-testid={pinned ? "affirmations-your-affirmation-eyebrow" : "affirmations-for-you-eyebrow"}>
            {pinned ? "Your affirmation" : "An affirmation for you"}
          </span>
          {category && (
            <span className="normal-case tracking-normal text-[hsl(var(--muted-foreground))]">
              · {category}
            </span>
          )}
        </div>
        <p
          className="mt-4 font-display italic text-2xl md:text-3xl leading-snug text-[hsl(var(--fg))]"
          data-testid={pinned ? "affirmations-your-affirmation-text" : "affirmations-for-you-text"}
        >
          “{text}”
        </p>
        {pinned && (
          <p className="mt-3 text-[13px] italic text-[hsl(var(--muted-foreground))]">
            Carry this with you.
          </p>
        )}

        <div className="mt-6 flex items-center gap-4 flex-wrap">
          <button
            type="button"
            onClick={() => onCarry(text)}
            data-testid="affirmations-moment-carry"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--clay))] hover:text-[hsl(var(--terracotta))] tab-link"
          >
            <Feather size={13} /> Carry into Journal
          </button>
          {pinned ? (
            <button
              type="button"
              onClick={onUnpin}
              data-testid="affirmations-moment-choose-another"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
            >
              Choose another
            </button>
          ) : (
            <button
              type="button"
              onClick={onPickAnother}
              disabled={rolling}
              data-testid="affirmations-moment-another"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
            >
              Another →
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
